/* eslint-disable max-lines */
import { Injectable, inject } from '@angular/core';

import { GuardedFeature } from '@core/feature-guard-legacy';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { I18nService } from '@core/i18n-legacy';

import type { PlaceholderData } from './placeholder';
import { Placeholder, PlaceholderCategory, PlaceholderId } from './placeholder';

export interface PlaceholderCollection {
  Basic: PlaceholderId[];
  Extended: PlaceholderId[];
}

export type PlaceholdersData = Partial<Record<PlaceholderId, PlaceholderData>>;

@Injectable({
  providedIn: 'root',
})
export class PlaceholderService {
  public static readonly Collection: PlaceholderCollection = {
    Basic: [
      PlaceholderId.FirstName,
      PlaceholderId.LastName,
      PlaceholderId.Candidate,
      PlaceholderId.JobOffer,
      PlaceholderId.Company,

      PlaceholderId.CandidateFirst,
      PlaceholderId.CandidateLast,
      PlaceholderId.CandidateFull,
      PlaceholderId.CandidateFullEmail,
    ],
    Extended: [
      PlaceholderId.FirstName,
      PlaceholderId.LastName,
      PlaceholderId.Candidate,
      PlaceholderId.EmailSender,
      PlaceholderId.JobOffer,
      PlaceholderId.Company,
      PlaceholderId.RecruiterPerJob,
      PlaceholderId.HiringManager,
      PlaceholderId.Department,

      PlaceholderId.CandidateFirst,
      PlaceholderId.CandidateLast,
      PlaceholderId.CandidateFull,
      PlaceholderId.CandidateFullEmail,
      PlaceholderId.CandidateGeneralTitle,
      PlaceholderId.CandidateProfessionalTitle,

      PlaceholderId.FormalGreetingTitlesLast,

      PlaceholderId.EmailSenderFirst,
      PlaceholderId.EmailSenderLast,
      PlaceholderId.EmailSenderFull,
      PlaceholderId.EmailSenderFullEmail,

      PlaceholderId.RecruiterPerJobFirst,
      PlaceholderId.RecruiterPerJobLast,
      PlaceholderId.RecruiterPerJobFull,
      PlaceholderId.RecruiterPerJobFullEmail,

      PlaceholderId.HiringManagerFirst,
      PlaceholderId.HiringManagerLast,
      PlaceholderId.HiringManagerFull,
      PlaceholderId.HiringManagerFullEmail,
    ],
  };
  private readonly i18n = inject(I18nService);
  private readonly featureSpecDataHelpers = inject(FeatureSpecFacade);

  private readonly hasCandidateSalutationFieldsAvailable =
    this.featureSpecDataHelpers.selectIsEntitled(GuardedFeature.CandidateSalutationFields);
  private readonly candidateSalutationFieldsPlaceholders = [
    PlaceholderId.CandidateGeneralTitle,
    PlaceholderId.CandidateProfessionalTitle,
    PlaceholderId.FormalGreetingTitlesLast,
  ];

  public readonly categoryPlaceholdersMap: Record<PlaceholderCategory, PlaceholderId[]> = {
    [PlaceholderCategory.Candidate]: [
      PlaceholderId.CandidateFirst,
      PlaceholderId.CandidateLast,
      PlaceholderId.CandidateFull,
      PlaceholderId.CandidateFullEmail,
      PlaceholderId.CandidateGeneralTitle,
      PlaceholderId.CandidateProfessionalTitle,
      PlaceholderId.FormalGreetingTitlesLast,
    ],
    [PlaceholderCategory.EmailSender]: [
      PlaceholderId.EmailSenderFirst,
      PlaceholderId.EmailSenderLast,
      PlaceholderId.EmailSenderFull,
      PlaceholderId.EmailSenderFullEmail,
    ],
    [PlaceholderCategory.Event]: [
      PlaceholderId.EventFull,
      PlaceholderId.EventLink,
      PlaceholderId.EventLocation,
      PlaceholderId.EventDate,
      PlaceholderId.EventType,
      PlaceholderId.EventInterviewers,
    ],
    [PlaceholderCategory.HiringManager]: [
      PlaceholderId.HiringManagerFirst,
      PlaceholderId.HiringManagerLast,
      PlaceholderId.HiringManagerFull,
      PlaceholderId.HiringManagerFullEmail,
    ],
    [PlaceholderCategory.Recruiter]: [
      PlaceholderId.RecruiterPerJobFirst,
      PlaceholderId.RecruiterPerJobLast,
      PlaceholderId.RecruiterPerJobFull,
      PlaceholderId.RecruiterPerJobFullEmail,
    ],
    [PlaceholderCategory.FormalGreetingsAndTitles]: [
      PlaceholderId.CandidateGeneralTitle,
      PlaceholderId.CandidateProfessionalTitle,
      PlaceholderId.FormalGreetingTitlesLast,
    ],
  };
  public readonly subcategoryPlaceholdersMap: Record<PlaceholderCategory, PlaceholderCategory[]> = {
    [PlaceholderCategory.Candidate]: [PlaceholderCategory.FormalGreetingsAndTitles],
    [PlaceholderCategory.EmailSender]: [],
    [PlaceholderCategory.Event]: [],
    [PlaceholderCategory.HiringManager]: [],
    [PlaceholderCategory.Recruiter]: [],
    [PlaceholderCategory.FormalGreetingsAndTitles]: [],
  };

  public deprecatedCollection = [
    PlaceholderId.FirstName,
    PlaceholderId.LastName,
    PlaceholderId.Candidate,
    PlaceholderId.EmailSender,
    PlaceholderId.RecruiterPerJob,
    PlaceholderId.HiringManager,
    PlaceholderId.EventTitle,
  ];

  public jobRelatedCollection = [
    PlaceholderId.RecruiterPerJobFirst,
    PlaceholderId.RecruiterPerJobLast,
    PlaceholderId.RecruiterPerJobFull,
    PlaceholderId.RecruiterPerJobFullEmail,

    PlaceholderId.HiringManagerFirst,
    PlaceholderId.HiringManagerLast,
    PlaceholderId.HiringManagerFull,
    PlaceholderId.HiringManagerFullEmail,

    PlaceholderId.RecruiterPerJob,
    PlaceholderId.HiringManager,
    PlaceholderId.Department,

    PlaceholderId.JobOffer,

    PlaceholderId.CandidateGeneralTitle,
    PlaceholderId.CandidateProfessionalTitle,
    PlaceholderId.FormalGreetingTitlesLast,
  ];

  public placeholderCategoryTranslationMap: Record<string, { name: string; desc: string }> = {
    [PlaceholderCategory.Candidate]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.candidate'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.candidate',
      ),
    },
    [PlaceholderCategory.EmailSender]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.email_sender'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.email_sender',
      ),
    },
    [PlaceholderCategory.Event]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.event'),
      desc: this.i18n.translate('app.email_templates.placeholder_categories_descriptions.event'),
    },
    [PlaceholderCategory.HiringManager]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.hiring_manager'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.hiring_manager',
      ),
    },
    [PlaceholderCategory.Recruiter]: {
      name: this.i18n.translate('app.email_templates.placeholder_categories.recruiter_per_job'),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.recruiter_per_job',
      ),
    },
    [PlaceholderCategory.FormalGreetingsAndTitles]: {
      name: this.i18n.translate(
        'app.email_templates.placeholder_categories.formal_greetings_and_titles',
      ),
      desc: this.i18n.translate(
        'app.email_templates.placeholder_categories_descriptions.formal_greetings_and_titles',
      ),
    },
  };

  private descriptions: { [key: string]: string } = {
    [PlaceholderId.FirstName]: this.i18n.translate('app.email_templates.first_name'),
    [PlaceholderId.LastName]: this.i18n.translate('app.email_templates.last_name'),
    [PlaceholderId.Candidate]: this.i18n.translate('app.email_templates.candidate'),
    [PlaceholderId.Company]: this.i18n.translate('app.email_templates.company'),
    [PlaceholderId.Recruiter]: this.i18n.translate('app.email_templates.recruiter'),
    [PlaceholderId.EmailSender]: this.i18n.translate('app.email_templates.email_sender_v2'),
    [PlaceholderId.RecruiterPerJob]: this.i18n.translate(
      'app.email_templates.recruiter_per_job_v2',
    ),
    [PlaceholderId.HiringManager]: this.i18n.translate('app.email_templates.hiring_manager_v2'),
    [PlaceholderId.JobOffer]: this.i18n.translate('app.email_templates.job_offer'),
    [PlaceholderId.Department]: this.i18n.translate('app.email_templates.department'),
    [PlaceholderId.EventTitle]: this.i18n.translate('app.email_templates.event_title_v2'),

    [PlaceholderId.CandidateFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.first',
    ),
    [PlaceholderId.CandidateLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.last',
    ),
    [PlaceholderId.CandidateFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.full',
    ),
    [PlaceholderId.CandidateFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.full_email',
    ),
    [PlaceholderId.CandidateGeneralTitle]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.general_title',
    ),
    [PlaceholderId.CandidateProfessionalTitle]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.candidate.professional_title',
    ),

    [PlaceholderId.FormalGreetingTitlesLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.formal_greeting.titles_last',
    ),

    [PlaceholderId.JobOfferAll]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.job_offer_all',
    ),

    [PlaceholderId.RecruiterPerJobFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.first',
    ),
    [PlaceholderId.RecruiterPerJobLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.last',
    ),
    [PlaceholderId.RecruiterPerJobFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.full',
    ),
    [PlaceholderId.RecruiterPerJobFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.recruiter_per_job.full_email',
    ),

    [PlaceholderId.HiringManagerFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.first',
    ),
    [PlaceholderId.HiringManagerLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.last',
    ),
    [PlaceholderId.HiringManagerFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.full',
    ),
    [PlaceholderId.HiringManagerFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.hiring_manager.full_email',
    ),

    [PlaceholderId.EventFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.full',
    ),
    [PlaceholderId.EventLink]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.link',
    ),
    [PlaceholderId.EventLocation]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.location',
    ),
    [PlaceholderId.EventDate]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.date',
    ),
    [PlaceholderId.EventType]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.type',
    ),
    [PlaceholderId.EventInterviewers]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.event.interviewers',
    ),

    [PlaceholderId.EmailSenderFirst]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.first',
    ),
    [PlaceholderId.EmailSenderLast]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.last',
    ),
    [PlaceholderId.EmailSenderFull]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.full',
    ),
    [PlaceholderId.EmailSenderFullEmail]: this.i18n.translate(
      'app.email_templates.placeholders_descriptions.email_sender.full_email',
    ),
  };

  public createCollection(
    collectionName: keyof PlaceholderCollection,
    data: PlaceholdersData = {},
  ): Placeholder[] {
    return PlaceholderService.Collection[collectionName]
      .filter(placeholderId =>
        this.candidateSalutationFieldsPlaceholders.includes(placeholderId)
          ? this.hasCandidateSalutationFieldsAvailable()
          : true,
      )
      .map(
        id =>
          new Placeholder(id, {
            value: null,
            description: this.descriptions[id],
            status: null,
            ...(data[id] || {}),
          }),
      );
  }

  public createCustomCollection(
    placeholderIds: PlaceholderId[],
    data: PlaceholdersData = {},
  ): Placeholder[] {
    return (placeholderIds || [])
      .filter(placeholderId =>
        this.candidateSalutationFieldsPlaceholders.includes(placeholderId)
          ? this.hasCandidateSalutationFieldsAvailable()
          : true,
      )
      .map(
        id =>
          new Placeholder(id, {
            value: id,
            description: this.descriptions[id],
            status: null,
            ...(data[id] || {}),
          }),
      );
  }

  public isCategoryVisible(placeholders: Placeholder[], category: PlaceholderCategory): boolean {
    return placeholders.some(placeholder =>
      placeholder.id !== 'empty'
        ? this.categoryPlaceholdersMap[category].includes(placeholder.id)
        : false,
    );
  }

  public isSubcategory(subcategory: PlaceholderCategory): boolean {
    return Object.values(PlaceholderCategory).some(category => {
      const subcategories = this.subcategoryPlaceholdersMap[category] || [];
      return subcategories.includes(subcategory);
    });
  }

  public filterPlaceholders(
    placeholders: Placeholder[],
    placeholderId: PlaceholderId,
  ): Placeholder[] {
    const placeholder = placeholders.find(placeholder => placeholder.id === placeholderId);
    return placeholder ? [placeholder] : [];
  }

  public filterPlaceholdersByCategory(
    placeholders: Placeholder[],
    category: PlaceholderCategory,
  ): Placeholder[] {
    return placeholders.filter(placeholder =>
      placeholder.id !== 'empty'
        ? this.categoryPlaceholdersMap[category].includes(placeholder.id)
        : false,
    );
  }
}
